import { Flex, Text } from '@lego/klik-ui';
import { FleetBadge } from '@/components/fleet/FleetBadge';
import { type RepositoryListResponse, useRepository } from '@/hooks/repository';
import { dateTimeFormat } from '@/utility/date';
import { createColumnHelper } from '../appTable';
import { dateRange } from '../filters/filterFunctions';

type RepositoryResponse = RepositoryListResponse['repositories'][number];

const columnHelper = createColumnHelper<RepositoryResponse>();

const RepositoryFleetsCell = ({
  repositoryId,
  workspaceId,
}: {
  repositoryId: number;
  workspaceId: number;
}) => {
  const { data } = useRepository(workspaceId, repositoryId);
  const uniqueFleetIds = [...new Set(data?.tags.flatMap((t) => t.fleets ?? []) ?? [])].sort(
    (a, b) => a - b
  );

  if (!data) return <Text color="slate.500">No data</Text>;

  return uniqueFleetIds.length > 0 ? (
    <Flex flexWrap="wrap" gap={1}>
      {uniqueFleetIds.map((id) => (
        <FleetBadge fleetId={id} key={id} workspaceId={workspaceId} />
      ))}
    </Flex>
  ) : (
    <Text color="slate.500">Unassigned</Text>
  );
};

export const getRepositoryTableColumns = (workspaceId: number) =>
  columnHelper.columns([
    columnHelper.accessor('id', {
      cell: (info) => (
        <Text fontFamily="mono" fontSize="sm">
          {info.getValue()}
        </Text>
      ),
      filterFn: 'includesString',
      header: 'ID',
      meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
      size: 96,
    }),
    columnHelper.accessor('name', {
      cell: (info) => info.getValue(),
      header: 'Name',
      meta: { filterVariant: { type: 'singleSearch' } },
      size: 128,
    }),
    columnHelper.accessor('createdAt', {
      cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
      filterFn: dateRange,
      header: 'Created At',
      meta: { filterVariant: { type: 'dateRange' } },
      size: 128,
    }),
    columnHelper.accessor('id', {
      id: 'fleets',
      enableSorting: false,
      header: 'Fleets',
      size: 200,
      cell: (info) => (
        <RepositoryFleetsCell repositoryId={info.getValue()} workspaceId={workspaceId} />
      ),
    }),
  ]);
